"use client"

import type { ReactNode } from "react"

import {
  HandoverOutcomesCard,
  HandoverReasonsCard,
  TakeoverReasonsCard,
} from "../sections/outcome-sections"
import { HandoverTiles, ServiceMetrics, TeamTable } from "../sections/service-metric-sections"
import {
  ErrorAlert,
  NoFinishedSessionsNotice,
  useFinishedSessions,
  type QualityTabProps,
} from "./tab-parts"

/** What became of the handovers, then how the team compares with the agent. */
export function HandoversTab(props: QualityTabProps) {
  const { quality } = props
  const { service, serviceError, outcomes, outcomesError, isEmpty } = useFinishedSessions(props)

  if (isEmpty) return <NoFinishedSessionsNotice />

  return (
    <div className="space-y-4 sm:space-y-6">
      {(!serviceError || quality !== "off") && (
        <PageSection title="Handovers">
          {/* A failed service query is announced once, under «AI vs your team». */}
          {!serviceError && <HandoverTiles data={service} />}
          {quality !== "off" &&
            (outcomesError ? (
              <ErrorAlert error={outcomesError} />
            ) : (
              <div className="grid gap-3 md:grid-cols-3 md:items-start">
                <HandoverReasonsCard data={outcomes} />
                <HandoverOutcomesCard data={outcomes} />
                <TakeoverReasonsCard
                  data={outcomes}
                  takeovers={service?.current.escalations.operatorTakeovers}
                />
              </div>
            ))}
        </PageSection>
      )}

      <PageSection title="AI vs your team" error={serviceError}>
        <ServiceMetrics data={service} />
        <TeamTable data={service} />
      </PageSection>
    </div>
  )
}

/**
 * A block with its own alert: the sections inside render skeletons while their data is
 * undefined, so a failed query would otherwise look like a load that never finishes.
 */
function PageSection({
  title,
  error,
  children,
}: {
  title: string;
  error?: Error | null;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {error ? <ErrorAlert error={error} /> : children}
    </section>
  )
}
